import { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { getGraphClient } from "../services/graphClient";

/**
 * Fetches the current user's colleagues from Microsoft 365.
 * Falls back to a manual list if the API call fails.
 */
const ALLOWED_EMAILS = [
  "gary@rpsigns.net",
  "carl@rpsigns.net",
  "james@rpsigns.net",
  "jim@rpsigns.net",
  "christine@rpsigns.net",
  "becca@rpsigns.net",
  "whitney@rpsigns.net",
  "graham@rpsigns.net",
];

export function useTeamMembers() {
  const { instance, accounts } = useMsal();
  const account = accounts[0];
  const [members, setMembers] = useState([]);

  useEffect(() => {
    if (!account) return;
    (async () => {
      try {
        const client = getGraphClient(instance, account);

        // Fetch org users, following pagination so nobody is missed.
        // (The old code capped at 25, which could drop allowed users
        //  who happened to sort past the first page.)
        let res = await client
          .api("/users")
          .select("id,displayName,mail,userPrincipalName")
          .top(999)
          .get();

        let users = res.value || [];
        let next = res["@odata.nextLink"];
        let guard = 0;
        while (next && guard < 50) {
          const page = await client.api(next).get();
          users = users.concat(page.value || []);
          next = page["@odata.nextLink"];
          guard++;
        }

        setMembers(
          users
            .filter((u) => u.displayName && u.mail)
            .filter((u) => ALLOWED_EMAILS.includes(u.mail.toLowerCase()))
            .map((u) => ({
              id: u.id,
              name: u.displayName,
              email: u.mail || u.userPrincipalName,
              initials: u.displayName
                .split(" ")
                .map((n) => n[0])
                .slice(0, 2)
                .join("")
                .toUpperCase(),
            }))
        );
      } catch {
        setMembers([]);
      }
    })();
  }, [account, instance]);

  return members;
}
